/**
 * Utility functions for image-related operations
 */

/**
 * Parse screenshots in the new format: array of arrays [[url, filename], ...]
 * Returns array of objects with url and filename properties
 * 
 * @param {Array|any} screenshotData - Screenshot data
 * @returns {Array<{url: string, filename: string|null}>} Array of screenshot objects
 */
export const parseScreenshots = (screenshotData) => {
  if (!screenshotData) {
    return [];
  }

  if (Array.isArray(screenshotData)) {
    return screenshotData
      .filter(Boolean)
      .map(item => {
        // New format: [url, filename]
        if (Array.isArray(item) && item.length >= 1) {
          return {
            url: item[0] || '',
            filename: item[1] || null
          };
        }
        // Old format: just a URL string
        if (typeof item === 'string') {
          return {
            url: item,
            filename: null
          };
        }
        return null;
      })
      .filter(Boolean)
      .filter(s => s.url && s.url.trim());
  }

  return [];
};

/**
 * Parse and separate image analysis content from post text
 * Image analysis sections start with "Image analysis for image number X:" pattern
 * 
 * @param {string} text - The full text that may contain image analysis
 * @returns {{postText: string, imageAnalysisSections: Array<{number: number, content: string}>}} Object with separated post text and array of image analysis sections
 */
export const separateImageAnalysis = (text) => {
  if (!text || typeof text !== 'string') {
    return {
      postText: text || '',
      imageAnalysisSections: []
    };
  }

  // Look for the pattern "Image analysis for image number X:" (case-insensitive)
  // This regex matches: "Image analysis for image number" followed by optional whitespace, a number, and a colon
  const imageAnalysisPattern = /Image\s+analysis\s+for\s+image\s+number\s+(\d+)\s*:/gi;
  
  // Find all matches first
  const matches = [];
  let match;
  while ((match = imageAnalysisPattern.exec(text)) !== null) {
    matches.push({
      index: match.index,
      number: parseInt(match[1], 10),
      fullMatch: match[0]
    });
  }
  
  if (matches.length === 0) {
    // No image analysis found, return the full text as post text
    return {
      postText: text,
      imageAnalysisSections: []
    };
  }
  
  // Extract post text (everything before the first image analysis)
  const postText = text.substring(0, matches[0].index).trim();
  
  // Extract each image analysis section
  const sections = [];
  for (let i = 0; i < matches.length; i++) {
    const currentMatch = matches[i];
    const nextMatch = matches[i + 1];
    
    // Section content starts at the current match index and ends at the next match (or end of text)
    const sectionStart = currentMatch.index;
    const sectionEnd = nextMatch ? nextMatch.index : text.length;
    const sectionContent = text.substring(sectionStart, sectionEnd).trim();
    
    if (sectionContent) {
      sections.push({
        number: currentMatch.number,
        content: sectionContent
      });
    }
  }

  return {
    postText: postText || '',
    imageAnalysisSections: sections
  };
};
